'use client';

import React from 'react';

import Image from 'next/image';
import Link from 'next/link';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { MarketplaceIcon } from '@/components/MarketplaceIcon';
import { useUserInfo } from '@/context/UserInfoContext';
import { DisplayConceptRef, DisplayProduct } from '@/lib/api';
import { API_URL } from '@/lib/consts';
import { sku_url } from '@/lib/utils';

interface ProductMiniCardProps {
  concept: DisplayConceptRef;
  product?: DisplayProduct;
  variant: 'floating' | 'table' | 'inline';
  comments?: string;
  onStarClick?: (cref: string, isInCollection: boolean) => void;
}

// TODO(2): There are lots of places in the code where we have a star icon like
// this that lets the user favorite products.  We should find all those and
// consolidate them into one component.  There will need to be some variation as
// it appears differently in different contexts, but a lot of the behavior
// should be shareable.
const StyledStarButton: React.FC<{
  isInCollection: boolean;
  onClick: (e: React.MouseEvent) => void;
  size?: 'small' | 'medium';
}> = ({ isInCollection, onClick, size = 'small' }) => {
  const theme = useTheme();

  return (
    <IconButton
      size={size}
      onClick={onClick}
      aria-label={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
      title={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
      sx={{
        position: 'absolute',
        top: 4,
        right: 4,
        padding: size === 'small' ? '4px' : '8px',
        color: 'gold',
        zIndex: 2,
        backgroundColor: 'rgba(128, 128, 128, 0.8)',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        transition: 'background-color 0.2s, transform 0.2s',
        '&:hover': {
          backgroundColor: 'rgba(128, 128, 128, 0.95)',
          transform: 'scale(1.1)',
        },
        [theme.breakpoints.down('sm')]: {
          top: 2,
          right: 2,
          padding: '2px',
          '& svg': {
            fontSize: 16,
          },
        },
      }}
    >
      {isInCollection ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
  );
};

export const ProductMiniCard: React.FC<ProductMiniCardProps> = ({
  concept,
  product,
  variant,
  comments,
  onStarClick,
}) => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const theme = useTheme();

  const isInCollection = React.useMemo(() => {
    if (!userInfo) return false;
    return userInfo.collections.some((collection) =>
      collection.products.some((p) => p.cref === concept.cref)
    );
  }, [userInfo, concept.cref]);

  const handleStarClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onStarClick) {
      onStarClick(concept.cref, isInCollection);
      return;
    }

    // Default star click handler
    if (!userInfo) return;

    try {
      const action = isInCollection ? 'remove' : 'add';
      const response = await fetch(`${API_URL}/user/update_collections`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          action: action,
          docs: [],
          products: [concept.cref],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update collection');
      }

      await reloadUserInfo();
    } catch (error) {
      console.error('Error updating collection:', error);
    }
  };

  const productUrl = sku_url(concept.cref);
  const showPrice = product && product.price;
  const showStar = concept.is_product && showPrice && userInfo;

  const imageElement = concept.img_url ? (
    <Image
      src={concept.img_url}
      alt={concept.mention || concept.display}
      fill
      style={{ objectFit: 'contain' }}
      sizes={variant === 'floating' ? '(max-width: 768px) 84px, 120px' : '120px'}
    />
  ) : (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
      }}
    >
      No Image
    </Box>
  );

  const priceDisplay = showPrice && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: variant === 'floating' ? 0.5 : variant === 'inline' ? 0 : 'auto',
        ml: 'auto',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '0.95rem',
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.85rem',
          },
        }}
      >
        {product.currency === 'USD' ? '$' : product.currency || '$'}
        {product.price!.toFixed(2)}
      </Typography>
      {product.marketplace && <MarketplaceIcon marketplace={product.marketplace} size={16} />}
    </Box>
  );

  // Common image box component
  const imageBox = (dimensions: any) => (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        backgroundColor: '#FFFFFF',
        ...dimensions,
      }}
    >
      {imageElement}
      {showStar && <StyledStarButton isInCollection={isInCollection} onClick={handleStarClick} />}
    </Box>
  );

  // Common content box component
  const contentBox = (styles: any, children: React.ReactNode) => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...styles,
      }}
    >
      {children}
    </Box>
  );

  // Common title typography
  const titleTypography = (lineClamp: number) => (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        fontSize: '0.9rem',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word', // Ensure long words wrap
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.8rem',
        },
      }}
    >
      {concept.display}
    </Typography>
  );

  if (variant === 'floating') {
    return (
      <Card
        elevation={2}
        sx={{
          float: 'right',
          clear: 'right',
          margin: theme.spacing(1.25, 0, 1.25, 1.25),
          width: 240,
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            width: 168,
            margin: theme.spacing(0.75, 0, 0.75, 0.75),
          },
        }}
      >
        <CardActionArea component={Link} href={productUrl}>
          <CardContent
            sx={{
              p: 0,
              display: 'flex',
              '&:last-child': {
                pb: 0,
              },
            }}
          >
            {imageBox({
              width: 120,
              height: 120,
              [theme.breakpoints.down('sm')]: {
                width: 84,
                height: 84,
              },
            })}
            {contentBox(
              {
                padding: theme.spacing(1),
                width: 120,
                [theme.breakpoints.down('sm')]: {
                  width: 84,
                  padding: theme.spacing(0.75),
                },
              },
              <>
                {titleTypography(4)}
                {priceDisplay}
              </>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  // Inline variant (for context panel)
  if (variant === 'inline') {
    return (
      <Card
        elevation={2}
        sx={{
          overflow: 'hidden',
        }}
      >
        <CardActionArea component={Link} href={productUrl}>
          <CardContent
            sx={{
              p: 0,
              display: 'flex',
              '&:last-child': {
                pb: 0,
              },
            }}
          >
            {imageBox({
              width: 64,
              height: 64,
              [theme.breakpoints.up('md')]: {
                width: 72,
                height: 72,
              },
            })}
            {contentBox(
              {
                padding: theme.spacing(1),
                justifyContent: 'space-between',
              },
              <>
                {titleTypography(3)}
                {priceDisplay}
              </>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  // Table variant
  return (
    <Card
      elevation={2}
      sx={{
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          elevation: 3,
        },
      }}
    >
      <CardActionArea component={Link} href={productUrl}>
        <CardContent
          sx={{
            p: 0,
            display: 'flex',
            '&:last-child': {
              pb: 0,
            },
          }}
        >
          {imageBox({
            width: 120,
            minHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.down('sm')]: {
              width: 84,
              minHeight: 84,
            },
          })}
          {contentBox(
            {
              padding: theme.spacing(1.5, 2),
              gap: theme.spacing(1),
              [theme.breakpoints.down('sm')]: {
                padding: theme.spacing(1, 1.5),
                gap: theme.spacing(0.75),
              },
            },
            <>
              {titleTypography(2)}
              {comments && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    fontSize: '0.85rem',
                    flex: 1,
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '0.8rem',
                    },
                  }}
                >
                  {comments}
                </Typography>
              )}
              {priceDisplay}
            </>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
